import { KeyboardArrowLeftRounded } from '@streamelements/frontend-icons';
import { Box, Text } from '@streamelements/frontend-ui';
import { createFileRoute, Link, Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import { ToolbarButton } from '../../../components/ToolbarButton';
import { StepsIndicatorItem, StepsIndicatorRoot } from '../-StepIndicator';
import { useMemo } from 'react';

export const Route = createFileRoute('/_authenticated/onboarding/setup-multistreaming/_layout')({
  component: SetupMultistreamingPageLayout,
});

/** The routes for the setup multistreaming page */
function useRoutes() {
  const router = useRouter();

  return useMemo(() => {
    return {
      index: router.buildLocation({ to: '/onboarding/setup-multistreaming' }),
      onboardingIndex: router.buildLocation({ to: '/onboarding' }),
      connect: router.buildLocation({
        to: '/onboarding/setup-multistreaming/connect',
        search: {
          provider: 'youtube' as never,
          createVerticalComposition: false,
        },
      }),
      success: router.buildLocation({
        to: '/onboarding/setup-multistreaming/success',
        search: {
          destinationId: '',
          provider: '' as never,
        },
      }),
      finish: router.buildLocation({
        to: '/onboarding/setup-multistreaming/finish',
      }),
    };
  }, [router]);
}

/** Where the back button takes us based on the current route */
function useInverseRoutesChain() {
  const routes = useRoutes();
  return useMemo(() => {
    return {
      index: routes.onboardingIndex,
      connect: routes.index,
      success: routes.index,
      finish: routes.index,
    };
  }, [routes]);
}

/** The name of the current route based on the pathname */
function useRoutesNames() {
  const routes = useRoutes();

  return useMemo(() => {
    return {
      [routes.index.pathname]: 'index' as const,
      [routes.success.pathname]: 'success' as const,
      [routes.connect.pathname]: 'connect' as const,
      [routes.finish.pathname]: 'finish' as const,
    };
  }, [routes]);
}

function SetupMultistreamingPageLayout() {
  const { matches } = useRouterState();

  const inverseRoutesChain = useInverseRoutesChain();
  const routesNames = useRoutesNames();

  const lastMatch = matches[matches.length - 1];
  const { fullPath } = lastMatch;
  // Router matches have trailing slash, but buildLocation doesn't
  // Just in case they change it in the future, we'll check for it
  const sliceEnd = fullPath.endsWith('/') ? -1 : undefined;
  const currentStep = routesNames[fullPath.slice(0, sliceEnd)];
  const previousStep = inverseRoutesChain[currentStep];

  return (
    <Box
      css={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
      }}
    >
      <Box
        css={{
          px: '0.5rem',
          py: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        <ToolbarButton css={{ background: 'none' }} aria-label="Back" asChild>
          <Link to={previousStep?.href ?? '/onboarding'}>
            <KeyboardArrowLeftRounded />
          </Link>
        </ToolbarButton>

        <Text.Body variant="caption" weight="bold">
          Setup multi-streaming
        </Text.Body>

        {currentStep !== 'finish' && (
          <StepsIndicatorRoot css={{ ml: 'auto' }}>
            <StepsIndicatorItem state={currentStep === 'index' ? 'active' : 'inactive'} />
            <StepsIndicatorItem
              state={currentStep === 'success' || currentStep === 'connect' ? 'active' : 'inactive'}
            />
          </StepsIndicatorRoot>
        )}
      </Box>

      <Outlet />
    </Box>
  );
}
